import type { Context } from '../models/types'
import type { Flow, FlowMetadata } from '../models/types'
import { EXPERIMENT_WINDOW_FALLBACK_KEY } from '../../helpers/experimentCookie'

export default function getJs(
  context: Context,
  flow: Flow,
  groupId: string,
  metadata: FlowMetadata | {},
  userData: Record<string, unknown>
) {
  if (!metadata) metadata = {}
  const pageTitle = `Embeddables${
    metadata && 'title' in metadata
      ? ': ' + metadata?.title
      : flow.name
      ? ': ' + flow.name
      : flow.title
      ? ': ' + flow.title
      : ''
  }`

  return `
    function (args) {
      //# sourceURL=renderer_eval_js_v3.ts
      //* Held outside the try so the catch can attribute the failure even if it throws early.
      var __embeddablesFlowId__ = ${context.env === 'ci' ? `'FL1FL2FL3'` : `'${flow.id}'`}
      //* Sentry is not loaded yet when this eval runs (SSR or /init). Defer logs into a window
      //* queue; the bundle flushes it on boot/load, and we also flush immediately if the hook is
      //* already exposed (eval after bundle — common on SSR hosts).
      var __embeddablesDeferLog__ = function (entry) {
        try {
          window.__EMBEDDABLES_EVAL_ERRORS__ = window.__EMBEDDABLES_EVAL_ERRORS__ || []
          window.__EMBEDDABLES_EVAL_ERRORS__.push(entry)
          if (typeof window.__EMBEDDABLES_FLUSH_EVAL_ERRORS__ === 'function') {
            window.__EMBEDDABLES_FLUSH_EVAL_ERRORS__({ flush_source: 'eval_v3' })
          }
        } catch (queueError) {}
      }
      try {
      let { element, embeddableData } = args || {}

      if (!element) element = document.querySelector('savvy[id="${flow.id}"]')

      // If the host element already has children (e.g. server-rendered HTML), skip innerHTML but continue setup.
      const alreadyRendered = element ? element.children.length > 0 : false

      const FLOW_ID = ${context.env === 'ci' ? `'FL1FL2FL3'` : `'${flow.id}'`}
      
      window.SavvyFlow = window.SavvyFlow || {}

      if (embeddableData) {
        window.SavvyFlow[FLOW_ID] = embeddableData.flow
        if (!alreadyRendered) element.innerHTML = embeddableData.html || ''
        } else {
          //* Fetch the flow JSON from the renderer backend and store it in the window.SavvyFlow object
        const rendererUrl = \`https://engine.embeddables.com/\${FLOW_ID}\`
        fetch(rendererUrl)
          .then(response => response.json())
          .then(data => {
            window.SavvyFlow[FLOW_ID] = data.flow
            if (!alreadyRendered) element.innerHTML = data.html || ''
          })
          .catch(error => {
            console.error('Error fetching flow JSON:', error)
          })
      }

      
      //* Here we merge all the user data (1° defaults, 2° local storage, 3° user data from backend, minus defaults) and update the local storage
      const allEmbeddablesUserData = JSON.parse(localStorage.getItem('SavvyFormUserData') || '{}')
      const localUserData = ${(!flow.forget_user_data && 'allEmbeddablesUserData[FLOW_ID]') || '{}'}
      const defaultUserData = ${JSON.stringify(flow.defaults?.userData || {})} || {}
      const userDataFromHandleRequestWithoutDefaults = ${JSON.stringify(userData)} || {}
      const fullUserData = { ...defaultUserData, ...localUserData, ...userDataFromHandleRequestWithoutDefaults }
      allEmbeddablesUserData[FLOW_ID] = fullUserData
      //* Diagnostic state for the write/read-back verification below. Does not
      //* change the write itself, which stays exactly as before.
      let storageWriteOk = true
      let storageWriteError = null
      let storageReadbackOk = true
      let storageReadbackError = null
      //* Starts as 'not_attempted' rather than 'ok' so a failed write is never
      //* indistinguishable from a genuinely successful read-back further down.
      let storageReadbackResult = 'not_attempted'
      let splitKeyCountReadback = 0
      //* Captured once right after SavvyFormUserData setItem so the sentinel value and
      //* __EMBEDDABLES_EVAL_WRITE_AT__ / eval_user_data_written timestamp stay aligned.
      let storageWriteAt = null
      //* Emits the eval_user_data_written diagnostic log with whatever write/read-back state
      //* has been captured so far. Called both on the happy path and right before re-throwing
      //* a write failure, so the diagnostic is never lost even when eval fails.
      const __embeddablesLogUserDataWritten__ = function () {
        const __embeddablesEvalWriteAt__ = storageWriteAt != null ? storageWriteAt : Date.now()
        //* Only stamp after a successful SavvyFormUserData write so connect does not report
        //* a non-null eval_to_connect_ms for do_not_store / failed-write sessions.
        if (storageWriteAt != null) {
          try {
            window.__EMBEDDABLES_EVAL_WRITE_AT__ = window.__EMBEDDABLES_EVAL_WRITE_AT__ || {}
            window.__EMBEDDABLES_EVAL_WRITE_AT__[FLOW_ID] = storageWriteAt
          } catch (_) {}
        }
        __embeddablesDeferLog__({
          scope: 'Eval connect',
          step: 'eval_user_data_written',
          flow_id: FLOW_ID,
          timestamp: __embeddablesEvalWriteAt__,
          props: {
            split_key_count: Object.keys(fullUserData).filter(function (key) { return key.startsWith('split_') }).length,
            split_key_count_readback: splitKeyCountReadback,
            storage_write_ok: storageWriteOk,
            storage_write_error: storageWriteError,
            storage_readback_ok: storageReadbackOk,
            storage_readback_error: storageReadbackError,
            storage_readback_result: storageReadbackResult,
            did_write: ${!flow.do_not_store_any_data},
            already_rendered: alreadyRendered,
          },
        })
      }
      ${
        !flow.do_not_store_any_data
          ? `
      try {
        localStorage.setItem("SavvyFormUserData", JSON.stringify(allEmbeddablesUserData))
        storageWriteAt = Date.now()
      } catch (writeError) {
        storageWriteOk = false
        storageWriteError = writeError && writeError.name ? writeError.name + ': ' + writeError.message : String(writeError)
        //* Record the diagnostic before re-throwing so callers (SSR host / init_eval) still
        //* observe the write failure exactly as before (see outer catch below).
        __embeddablesLogUserDataWritten__()
        throw writeError
      }
      try {
        localStorage.setItem("EmbeddablesStorageSentinel", String(storageWriteAt))
      } catch (_) {}
      if (storageWriteOk) {
        let readBackRaw = null
        try {
          readBackRaw = localStorage.getItem('SavvyFormUserData')
        } catch (readGetError) {
          storageReadbackOk = false
          storageReadbackResult = 'read_error'
          storageReadbackError = readGetError && readGetError.name ? readGetError.name + ': ' + readGetError.message : String(readGetError)
        }
        if (storageReadbackOk) {
          if (readBackRaw === null) {
            storageReadbackResult = 'null'
          } else {
            try {
              const readBackData = JSON.parse(readBackRaw || '{}')
              const readBackFlowData = readBackData[FLOW_ID]
              if (!readBackFlowData) {
                storageReadbackResult = 'missing_flow'
              } else {
                storageReadbackResult = 'ok'
                splitKeyCountReadback = Object.keys(readBackFlowData).filter(function (key) { return key.startsWith('split_') }).length
              }
            } catch (parseError) {
              storageReadbackOk = false
              storageReadbackResult = 'parse_error'
              storageReadbackError = parseError && parseError.name ? parseError.name + ': ' + parseError.message : String(parseError)
            }
          }
        }
      }
      //* Publish split_* only (never the full user-data blob) to a window fallback so
      //* flow_controller.connect() can recover the assignment if its SavvyFormUserData read
      //* below arrives empty. Same gate as the localStorage write above — never runs when
      //* do_not_store_any_data suppresses storage. The key comes from EXPERIMENT_WINDOW_FALLBACK_KEY
      //* so this eval string can never drift from the typed read side in experimentCookie.ts.
      try {
        var splitDataForWindowFallback = {}
        Object.keys(fullUserData).forEach(function (key) {
          if (key.startsWith('split_')) splitDataForWindowFallback[key] = fullUserData[key]
        })
        if (Object.keys(splitDataForWindowFallback).length > 0) {
          window[${JSON.stringify(EXPERIMENT_WINDOW_FALLBACK_KEY)}] = window[${JSON.stringify(
            EXPERIMENT_WINDOW_FALLBACK_KEY
          )}] || {}
          window[${JSON.stringify(EXPERIMENT_WINDOW_FALLBACK_KEY)}][FLOW_ID] = splitDataForWindowFallback
        }
      } catch (windowFallbackError) {}
      `
          : ''
      }
      //* Timestamp the eval write so we can compare it against flow_controller connect's read
      //* (load_user_data_before / after). Deferred because Sentry is not loaded at eval time.
      __embeddablesLogUserDataWritten__()

      ${
        flow.store_experiments_in_cookies
          ? `
      // Set cookies for any Experiments (User Data keys starting with split_)
      const experimentData = {}
      Object.keys(fullUserData).forEach(key => {
        if (key.startsWith('split_')) {
          experimentData[key] = fullUserData[key]
        }
      })
      if (Object.keys(experimentData).length > 0) {
        document.cookie = \`Embeddables.\${FLOW_ID}=\${JSON.stringify(experimentData)}; path=/\`
      }
      `
          : ''
      }

      const urlParams = new URLSearchParams(window.location.search)

      // Load workbench when ?workbench=true or ?embeddables_workbench=true (scripts must be injected dynamically; innerHTML doesn't run them)
      if (urlParams.get('workbench') === 'true' || urlParams.get('embeddables_workbench') === 'true') {
        window.__EMBEDDABLES_WORKBENCH = window.__EMBEDDABLES_WORKBENCH || {}
        window.__EMBEDDABLES_WORKBENCH.embeddableId = FLOW_ID
        if (!document.querySelector('#__embeddables_workbench_root')) {
          const root = document.createElement('div')
          root.id = '__embeddables_workbench_root'
          element.parentNode.insertBefore(root, element.nextSibling)
        }
        const loadWorkbench = () => {
          if (typeof window.__embeddables_bootstrap_workbench === 'function') {
            window.__embeddables_bootstrap_workbench({ embeddableId: FLOW_ID })
          }
        }
        const workbenchDomain = urlParams.get('workbench_domain')
        let workbenchOrigin = 'https://embeddables-workbench.heysavvy.workers.dev'
        if (workbenchDomain) {
          try {
            const normalized = workbenchDomain.startsWith('http') ? workbenchDomain : 'https://' + workbenchDomain
            const parsedUrl = new URL(normalized)
            if (parsedUrl.protocol === 'https:' && (parsedUrl.hostname.endsWith('heysavvy.workers.dev') || parsedUrl.hostname === 'localhost')) {
              workbenchOrigin = parsedUrl.origin
            } else {
              console.error('[Embeddables] Invalid workbench_domain "' + workbenchDomain + '": must use HTTPS and hostname must end with heysavvy.workers.dev. Falling back to default workbench origin.')
            }
          } catch (e) {
            console.error('[Embeddables] Invalid workbench_domain "' + workbenchDomain + '": could not parse as a valid URL. Falling back to default workbench origin.')
          }
        }
        if (!document.querySelector('script[data-embeddables-workbench]')) {
          const script = document.createElement('script')
          script.src = workbenchOrigin + '/workbench.js'
          script.setAttribute('data-embeddables-workbench', 'true')
          script.onload = loadWorkbench
          document.head.appendChild(script)
        } else {
          loadWorkbench()
        }
      }

      const breakpoints = [${[520, 720]
        .concat((flow.breakpoints || []).map((b) => b.max_width))
        .toString()}]
      const entireFlowEl = element.querySelector('.Flow-EntireFlow')
      const currentBreakpointGroups = breakpoints.filter((b) => entireFlowEl.offsetWidth <= b)
      const attrValue = currentBreakpointGroups.map((b) => \`\${b}px\`).join(' ')
      entireFlowEl.setAttribute('max-width', attrValue)

      const editingHostnames = ['edit.embeddables.com']
      const editingUrlRegexes = [
        /^https?:\\/\\/app\\.embeddables\\.com\\/edit\\/.*(\\?.*)?$/,
        /^https?:\\/\\/old\\.embeddables\\.com\\/edit\\/.*(\\?.*)?$/,
        /^https?:\\/\\/radar\\.embeddables\\.com\\/edit\\/.*(\\?.*)?$/,
        /^https?:\\/\\/build\\.embeddables\\.com\\/edit\\/.*(\\?.*)?$/,
        /^https?:\\/\\/embeddables-.+\\.vercel\\.app\\/edit\\/.*(\\?.*)?$/,
      ]

      if (window.location.host.endsWith('trysavvy.com') || window.location.hostname === 'preview.embeddables.com' || window.location.href.includes('app.embeddables.com/edit') || window.location.host.endsWith('cidemo.co') || window.location.host === 'localhost:9000' || window.location.host === 'localhost:8787') {
        if(FLOW_ID==='${flow.id}') document.title = '${pageTitle.replaceAll("'", "\\'")}'
      } 

      // Create savvy-global element with data-controller="global"
      if (!document.querySelector('savvy-global')) {
        const savvyGlobal = document.createElement('savvy-global')
        savvyGlobal.setAttribute('data-controller', 'global')
        document.body.appendChild(savvyGlobal)
      }
      
      // Create savvy-editor element with data-controller="global-edit"
      const isEditingMode = (urlParams.get('savvy_editing') || editingHostnames.includes(window.location.hostname) || editingUrlRegexes.some((regex) => regex.test(window.location.href)))
      if (isEditingMode && !document.querySelector('savvy-editor')) {
        // Create savvy-editor element with data-controller="global-edit"
        const savvyEditor = document.createElement('savvy-editor')
        savvyEditor.setAttribute('data-controller', 'global-edit')
        document.body.appendChild(savvyEditor)
      }

      if (!window.Savvy) window.Savvy = {}
      if (!window.Savvy.group_id) window.Savvy.group_id = '${groupId}'
      if (!window.Savvy.project_id) window.Savvy.project_id = ${
        (metadata as FlowMetadata).project_id
          ? `'${(metadata as FlowMetadata).project_id}'`
          : 'null'
      }
      
      if (!window.Savvy.flow_metadata) window.Savvy.flow_metadata = {}
      if (!window.Savvy.flow_metadata[FLOW_ID]) window.Savvy.flow_metadata[FLOW_ID] = ${JSON.stringify(
        {
          ...metadata,
          flow_id: (metadata as FlowMetadata).id,
        }
      )}
  

      // Wrap all the savvy elements in the DOM
      const scriptElements = document.querySelectorAll("script");
      scriptElements?.forEach(script => {
        if (script.textContent.includes("getBundleSrc") && script.previousSibling?.nodeType!==Node.COMMENT_NODE && !script.previousSibling?.textContent?.includes("Embeddables")) {
          const startComment = document.createComment("START: Embeddables (formerly Savvy) Script - https://embeddables.com");
          const endComment = document.createComment("END: Embeddables (formerly Savvy) Script - https://embeddables.com");
          script.parentNode.insertBefore(startComment, script);
          script.parentNode.insertBefore(endComment, script.nextSibling);
        }
      });

      // Get all the <savvy> and <savvy-global> elements in the DOM
      const savvyElements = document.querySelectorAll("savvy, savvy-global");

      if(!isEditingMode) {
        console.log('%cThis page was built using Embeddables', 'font-size: 14px; font-weight:500;font-family: "Consolas", sans-serif; text-shadow: 1px 1px 0px rgba(0,0,0,0.6);text-decoration: none;  background-color: #7144F2; color: #F7F5ED; padding:6px 18px; border-radius: 5px; box-shadow: 10px 10px 0px 3px rgba(0,0,0,0.75);');
        console.log('%c➡  https://embeddables.com', 'font-size: 14px; font-weight:500;font-family: "Consolas", sans-serif; text-decoration: none; padding:6px 0px; border-radius: 5px; box-shadow: 10px 10px 0px 3px rgba(0,0,0,0.75);');
      }

      savvyElements?.forEach(element => {
        if (element.previousSibling?.nodeType===Node.COMMENT_NODE && element.previousSibling?.textContent?.includes("Embeddables")) return;
        const startComment = document.createComment("START: Embeddables (formerly Savvy) Embed Code - https://embeddables.com");
        const endComment = document.createComment("END: Embeddables (formerly Savvy) Embed Code - https://embeddables.com");
        element.parentNode.insertBefore(startComment, element);
        element.parentNode.insertBefore(endComment, element.nextSibling);
      });
      } catch (error) {
        __embeddablesDeferLog__({
          scope: 'Eval connect',
          step: 'eval_connect_failed',
          flow_id: __embeddablesFlowId__,
          message: error && error.message ? error.message : String(error),
          stack: error && error.stack ? error.stack : undefined,
          timestamp: Date.now(),
        })
        //* Re-throw so callers (SSR host / init_eval) still observe the failure after telemetry is queued.
        throw error
      }
    }
  `
}
